// Playbooks K7, the cards on Triage (module 10 U274, typed by Mike on 5 Oct: "Both a tab and also cards on triage."; U275).
// One card for each item that waits to be placed, on the Triage of every approver who signs in (Mike; Conor once he has),
// drawn at the bottom of the ranked list (after the quick wins until REQ-2026-10-13-16 removed them), so never above a client's own item (Cowork's reading of U274, reversible). The card is
// the To allocate tab's item in the triage card's grammar: the same queue (GET /api/playbooks/to-allocate reads the tab's
// book), the same options in the same order with Cobrain's best match as the lead, the same presses on the same route,
// and the same words (book.ts). A press clears the card for every approver, since the item no longer waits; its undo, in
// Triage's strip, brings it back for all of them (D62). "Why is this here?" says where Cobrain picked it up. Since K8 an
// objection Cobrain heard in a message or call is an item of its own, on the card as on the tab: the client's words, where
// they were heard, what our side answered, and the same four options on its own route (B2143).
import { CardFrame, Chip, ActionSelect, ActionInput, ActionTextarea, ActionButton, ActionRow } from '../parts/index.js';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useLiveRefresh } from '../live.js';
import { PlaybooksError, playbooksApi, type BookObjection, type PlaybookBook } from './api.js';
import { ALLOCATE_SAID, itemSource, listedInUse, NAME_MAX, nameOf, newObjection, raisedCount, raisedWords, waitingOf, type Item } from './book.js';
import './allocate-card.css';

/** What a press tells Triage's strip, with the undo that puts the item back where it was. */
export interface AllocateSaid { words: string; undo: (() => Promise<void>) | null }

/**
 * The queue for one store, read when Triage opens and whenever the person comes back to it (as the ten is). An item placed
 * here is hidden at once and stays hidden until the next read says it waits again (an undo). A read that fails keeps what
 * is on the screen; a reader who is not an approver, and an approver while nothing waits, gets no book and no cards.
 */
export function useToAllocate(store: string | null): { book: PlaybookBook | null; items: Item[]; reload: () => Promise<void>; hide: (id: string) => void; show: (id: string) => void } {
  const [book, setBook] = useState<PlaybookBook | null>(null);
  const [hidden, setHidden] = useState<ReadonlySet<string>>(new Set());
  const seq = useRef(0);
  const reload = useCallback(async () => {
    if (!store) return;
    const n = ++seq.current;
    try {
      const r = await playbooksApi.toAllocate(store);
      if (n !== seq.current) return;   // a later read is in the air: it answers
      setBook(r.canApprove ? r.book : null);
      setHidden(new Set());
    } catch {
      // The cards are the tab's too: a read that does not land leaves them as they were, and the tab still has them.
    }
  }, [store]);
  useEffect(() => { void reload(); }, [reload]);
  useLiveRefresh(() => { void reload(); });
  const items = book ? waitingOf(book).filter((i) => !hidden.has(i.key)) : [];
  const hide = useCallback((id: string) => setHidden((h) => new Set([...h, id])), []);
  const show = useCallback((id: string) => setHidden((h) => { const n = new Set(h); n.delete(id); return n; }), []);
  return { book, items, reload, hide, show };
}

/** Cobrain's best match, as the tab's card words it (B2059; Mike, 5 Oct about 09:10 CEST: "Fine (Recommended)"). */
export function MatchLine({ i }: { i: Item }) {
  return i.match ? <>Cobrain's best match: <b>{i.match.name}</b>.{i.why ? ` ${i.why}` : ''}</>
    : i.matched ? <>Cobrain found nothing on the list that answers it.{i.why ? ` ${i.why}` : ''}</>
      : <>Cobrain has not matched it to the list yet.</>;
}

export type Press = { place: string } | { list: { family: string | null; words?: string; name?: string } } | { bin: true };

/** One press on an item, on its own route (a type's, or an objection Cobrain heard, K8), and the undo of that press. */
export async function pressItem(store: string, i: Item, how: Press): Promise<() => Promise<void>> {
  if (i.s) {
    const s = i.s;
    await playbooksApi.allocateHeard(store, s.id, how as Parameters<typeof playbooksApi.allocateHeard>[2]);
    return async () => { await playbooksApi.allocateHeard(store, s.id, { undo: true }); };
  }
  const o = i.o!;
  await playbooksApi.allocate(store, o.id, how);
  return async () => { await playbooksApi.allocate(store, o.id, { undo: true }); };
}

/** What a press says, for a type or for what Cobrain heard (K4's words for the same act on a heard line, U267). */
export const placedSaid = (i: Item, under: string): string => (i.s ? ALLOCATE_SAID.heardPlaced(under, i.s.answered !== null) : ALLOCATE_SAID.placed(under));
export const binnedSaid = (i: Item): string => (i.s ? ALLOCATE_SAID.heardBinned : ALLOCATE_SAID.binned(i.name));

type Open = 'another' | 'new' | 'why' | null;

export function AllocateCard({ store, book, i, onSaid, onPlaced, onBack, open: go }: {
  store: string; book: PlaybookBook; i: Item;
  onSaid: (s: AllocateSaid) => void;
  /** The press landed: the card leaves, for this reader at once and for the others on their next read. */
  onPlaced: (id: string) => void;
  /** The undo landed: the card comes back. */
  onBack: (id: string) => Promise<void>;
  open: (path: string) => void;
}) {
  const [open, setOpen] = useState<Open>(null);
  const [under, setUnder] = useState('');
  const [family, setFamily] = useState(i.s?.family ?? '');
  const [words, setWords] = useState(i.s ? i.s.answered ?? '' : i.answer?.body ?? '');
  const [name, setName] = useState(nameOf(i));
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState<string | null>(null);
  const list = listedInUse(book);
  const toggle = (o: Open) => { setFailed(null); setOpen((x) => (x === o ? null : o)); };
  // One lead on a card: an open form's own button is it, so the row gives its lead up while one is open.
  const lead = open === null || open === 'why';

  const act = async (how: Press, said: string) => {
    setFailed(null);
    setBusy(true);
    let undo: () => Promise<void>;
    try {
      undo = await pressItem(store, i, how);
    } catch (e: unknown) {
      setFailed(e instanceof PlaybooksError ? `Not changed: ${e.message}.` : 'Not changed: Cobrain could not save it. Nothing changed.');
      setBusy(false);
      return;
    }
    onPlaced(i.key);
    onSaid({ words: said, undo: async () => { await undo(); await onBack(i.key); } });
  };
  const placeUnder = (o: BookObjection) => void act({ place: o.id }, placedSaid(i, o.name));
  const meeting = i.s?.heard?.meetingId ? `/meetings/${encodeURIComponent(i.s.heard.meetingId)}` : null;
  const makeIt = () => {
    const made = newObjection(i, name, family, words);
    if (!made) { setFailed('Not changed: give it a name.'); return; }
    void act(made.how, made.said);
  };

  return (
    <CardFrame className="alloc" data-alloc={i.key} aria-label={`To allocate: ${i.name}`}>
      <div className="meta"><Chip>To allocate</Chip><span>{raisedCount(i)}</span></div>
      <div className="headrow"><p className="head">{i.s ? `“${i.name}”` : i.name}</p></div>
      {i.matched || i.match ? <p className="gist alloc-match"><MatchLine i={i} /></p> : null}
      <ActionRow actions={[
        ...(i.match ? [{ key: 'match', label: <>Put under “<span className="nm">{i.match.name}</span>”</>, variant: lead ? 'lead' as const : 'plain' as const,
          disabled: busy, title: `Put under “${i.match.name}”`, onClick: () => placeUnder(i.match!) }] : []),
        { key: 'new', label: 'New objection', variant: !i.match && lead ? 'lead' : 'plain', disabled: busy, 'aria-expanded': open === 'new', onClick: () => toggle('new') },
        { key: 'another', label: 'Under another', disabled: busy, 'aria-expanded': open === 'another', onClick: () => { toggle('another'); setUnder(''); } },
        { key: 'bin', label: 'Bin it', disabled: busy, onClick: () => void act({ bin: true }, binnedSaid(i)) },
      ]} />
      {open === 'another' ? (
        <div className="state" data-state="another">
          <div className="delegate-row">
            <label className="field"><span className="note">Put it under</span>
              <ActionSelect aria-label="Put it under" value={under} onChange={(e) => setUnder(e.target.value)}>
                <option value="">Pick one of the list</option>
                {list.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
              </ActionSelect>
            </label>
            <ActionButton type="button" className="opt lead" disabled={busy || !under} onClick={() => { const o = list.find((x) => x.id === under); if (o) placeUnder(o); }}>Put under</ActionButton>
          </div>
        </div>
      ) : null}
      {open === 'new' ? (
        <div className="state alloc-new" data-state="new">
          <label className="field wide"><span className="note">Name</span>
            <ActionInput type="text" value={name} maxLength={NAME_MAX} onChange={(e) => setName(e.target.value)} />
          </label>
          <label className="field"><span className="note">Product</span>
            <ActionSelect value={family} onChange={(e) => setFamily(e.target.value)}>
              <option value="">Every product</option>
              {book.families.map((f) => <option key={f} value={f}>{f}</option>)}
            </ActionSelect>
          </label>
          <label className="field wide alloc-words"><span className="note">Our answer</span>
            <ActionTextarea rows={3} value={words} onChange={(e) => setWords(e.target.value)} placeholder="Leave empty if it needs words" />
          </label>
          <ActionButton type="button" className="opt lead" disabled={busy} onClick={makeIt}>Make it an objection</ActionButton>
        </div>
      ) : null}
      {failed ? <p className="error" role="alert">{failed}</p> : null}
      <ActionRow variant="standing" actions={[
        { key: 'why', label: 'Why is this here?', opensOnly: true, className: open === 'why' ? 'on' : undefined, 'aria-expanded': open === 'why', onClick: () => toggle('why') },
        { key: 'playbooks', label: 'Open in Playbooks', opensOnly: true, onClick: () => go(`/playbooks?tab=allocate&q=${encodeURIComponent(i.name.slice(0, 60))}`) },
        ...(meeting ? [{ key: 'call', label: 'Open the call', opensOnly: true, onClick: () => go(meeting) }] : []),
      ]} />
      {open === 'why' ? (
        <div className="why">
          <p>Cobrain picked these up from the SBC show notes and the team's conversations. Nothing here is used until one of the approvers places it, and placing it is the approval.</p>
          <ul>
            <li>{itemSource(i)}{raisedWords(i)}.</li>
            {i.answer ? <li>The usual answer it came with: “{i.answer.body}”</li> : null}
            {i.s?.answered ? <li>Our side answered: “{i.s.answered}”</li> : null}
            {i.said.slice(0, 2).map((x) => <li key={x.dealId}>{x.account}: “{x.said}”</li>)}
            {book.approvers.length > 0 ? <li>On the Triage of each approver ({book.approvers.join(', ')}); the first to place it clears it for all.</li> : null}
          </ul>
        </div>
      ) : null}
    </CardFrame>
  );
}
